import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniOtpInput } from '@/design-system/components/inputs';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { useNavigation } from '@react-navigation/native';
import { LayoutContext } from 'contexts/LayoutContext';
import { ScrollView, SxProp, View } from 'dripsy';
import { isEmpty, isString } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStackRoutes } from 'types';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import { PageHeaderModalNative } from '@/features/bill-payment/screens/components/PageHeaderModalNative';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { OtpInput, OtpVerifyType, UnionType } from '@/features/bill-payment/enum';
import { useInstructions } from '@/features/bill-payment/hooks/useUnionPay.hook';
import { errorParser } from '@/features/bill-payment/screens/otp/type';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { LayoutChangeEvent } from 'react-native';
import { usePayout } from '@/features/account-management/screens/details/hooks/usePayouts.hook';
import { CreatePayoutBodyDestination, CreatePayoutBodyPurpose, CreatePayoutBodySourceOfFunds } from '@utx/utx-access-client';

export interface TWOFANativeScreenProps {
  onBack?: (flag?: boolean) => void;
  wallet?: any;
  calcFee?: any;
  beneficiaryInformation?: any;
  unionType?: UnionType.PAYMENT | UnionType.ACCOUNT;
  refresh?: () => void;
  navigationNavigate?: string;
  navigationScreen?: string;
}

export const TWOFANativeScreen: React.FC<TWOFANativeScreenProps> = (props) => {
  const {
    onBack,
    wallet,
    calcFee,
    beneficiaryInformation,
    unionType,
    refresh,
    navigationNavigate = RootStackRoutes.BillPayment,
    navigationScreen = BillPaymentStackRoutes.UnionPay
  } = props;
  const { t } = useTranslation('global-bill-payment');
  const layout = useContext(LayoutContext);
  const navigationAny = useNavigation<any>();

  const { runSubmit } = useInstructions();
  const postInstructionsSubmit = (
    instructionId: string,
    amount: string,
    asset: string,
    purpose: string,
    remittancePurpose: string,
    fundSource: string,
    feeSnapshotId: string,
    accountId: string,
    destination: any,
    token: string,
    verifyType: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    runSubmit(
      {
        intent: 'REMITTANCE',
        instructionId: instructionId,
        amount: amount,
        asset: asset,
        purpose: purpose,
        remittancePurpose: remittancePurpose,
        fundSource: fundSource,
        feeSnapshotId: feeSnapshotId,
        method: 'UNIONPAY',
        accountId: accountId,
        destination: { type: 'UNIONPAY', attributes: destination },
        token: token,
        verifyType: verifyType
      },
      onSuccess,
      onError
    );
  };

  const { runPayoutSubmit } = usePayout();
  const postPayoutSubmit = (
    destination: CreatePayoutBodyDestination,
    quoteId: string,
    purpose: CreatePayoutBodyPurpose,
    sourceOfFunds: CreatePayoutBodySourceOfFunds,
    accountId: string,
    remarks: string,
    token: string,
    verifyType: number,
    onSuccess?: (data: any) => void,
    onError?: (error: any) => void
  ) => {
    runPayoutSubmit(
      {
        method: 'UNIONPAY',
        destination: destination,
        quoteId: quoteId,
        purpose: purpose,
        sourceOfFunds: sourceOfFunds,
        accountId: accountId,
        remarks: remarks,
        token: token,
        verifyType: verifyType
      },
      onSuccess,
      onError
    );
  };

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [pageState, setPageState] = useState<{
    ready?: boolean;
    code?: string;
    errorMessage?: string;
    requestAgain?: boolean;
  }>({});
  const [alertMessage, setAlertMessage] = useState('');
  const onConfirm = (code?: string) => {
    setPageState({ ...pageState, errorMessage: '' });
    setAlertMessage('');
    let otpCode = code || (pageState && pageState.code ? pageState.code : '');
    if (!pageState.ready) {
      setPageState({ ...pageState, errorMessage: t('global-bill-payment::otp.error--otp-invalid') });
    } else {
      let destination = {};
      if (beneficiaryInformation?.account?.id) {
        destination = {
          transferPeerId: beneficiaryInformation?.account?.id,
          firstName: beneficiaryInformation?.account?.attributes?.receiverFirstName,
          firstNameCN: beneficiaryInformation?.account?.attributes?.receiverFirstNameCN,
          surname: beneficiaryInformation?.account?.attributes?.receiverSurname,
          surnameCN: beneficiaryInformation?.account?.attributes?.receiverSurnameCN,
          accountNumber: beneficiaryInformation?.account?.attributes?.receiverAccountNumber,
          address: beneficiaryInformation?.account?.attributes?.receiverAddress,
          label: beneficiaryInformation?.account?.label
        };
      } else {
        destination = {
          firstName: beneficiaryInformation?.account?.firstNameEnglish,
          firstNameCN: beneficiaryInformation?.account?.firstNameChinese,
          surname: beneficiaryInformation?.account?.lastNameEnglish,
          surnameCN: beneficiaryInformation?.account?.lastNameChinese,
          accountNumber: beneficiaryInformation?.account?.accountNumber,
          address: beneficiaryInformation?.account?.accountHolderAddress,
          saveTransferPeer: beneficiaryInformation?.account?.isSave,
          label: beneficiaryInformation?.account?.isSave ? beneficiaryInformation?.account?.label : undefined,
          transferPeerId: undefined
        };
      }

      setIsLoadingSubmit(true);
      if (unionType == UnionType.PAYMENT) {
        postInstructionsSubmit(
          calcFee?.id,
          calcFee?.amount?.toString(),
          calcFee?.feeCurrency,
          beneficiaryInformation?.purpose,
          beneficiaryInformation?.purpose,
          beneficiaryInformation?.fundSource,
          calcFee?.feeSnapshotId,
          wallet?.id,
          destination,
          otpCode,
          OtpVerifyType.GOOGLE_OTP,
          (data: any) => {
            setIsLoadingSubmit(false);
            let refId = data?.data?.refId;
            navigationAny.navigate(navigationNavigate, {
              screen: navigationScreen,
              params: { screen: UnionPayStackRoutes.Placed, onBack, wallet, calcFee, beneficiaryInformation, refId }
            });
          },
          (error: any) => {
            setIsLoadingSubmit(false);
            const data: any = error.response?.data;
            const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
            setPageState({ ...pageState, errorMessage: errorMessage });
          }
        );
      } else if (unionType == UnionType.ACCOUNT) {
        postPayoutSubmit(
          {
            accountNumber: beneficiaryInformation?.account?.accountNumber,
            firstName: beneficiaryInformation?.account?.firstNameEnglish,
            firstNameCN: beneficiaryInformation?.account?.firstNameChinese,
            surname: beneficiaryInformation?.account?.lastNameEnglish,
            surnameCN: beneficiaryInformation?.account?.lastNameChinese,
            address: beneficiaryInformation?.account?.accountHolderAddress
          },
          calcFee?.id,
          beneficiaryInformation?.purpose,
          beneficiaryInformation?.fundSource,
          wallet?.id,
          beneficiaryInformation?.remark,
          otpCode,
          OtpVerifyType.GOOGLE_OTP,
          (data: any) => {
            setIsLoadingSubmit(false);
            let refId = data?.data?.refId;
            navigationAny.navigate(navigationNavigate, {
              screen: navigationScreen,
              params: { screen: UnionPayStackRoutes.Placed, onBack, wallet, calcFee, beneficiaryInformation, refId, unionType, refresh }
            });
          },
          (error: any) => {
            setIsLoadingSubmit(false);
            const data: any = error.response?.data;
            const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
            setPageState({ ...pageState, errorMessage: errorMessage });
          }
        );
      }
    }
  };
  useEffect(() => {
    if (pageState && pageState.code && pageState.ready) {
      onConfirm(pageState.code);
    }
  }, [pageState?.code, pageState?.ready]);

  const [sizeWidthButton, setSizeWithButton] = useState<number>(0);
  const onLayoutLocal = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setSizeWithButton(width);
  };

  const onCancel = () => {
    onBack && onBack();
  };

  return (
    <View sx={sxStyles.container}>
      <PageHeaderModalNative>
        <View sx={{ px: '$4', my: '$5' }}>
          <View sx={{ position: 'absolute', top: 0, left: 12, zIndex: 2 }}>
            <UniIconButton
              icon="arrowLeft"
              options={{ iconStyles: { themeColor: '$white' } }}
              onPress={() => {
                onBack && onBack();
              }}></UniIconButton>
          </View>
          <View sx={{ textAlign: 'center', width: '100%' }}>
            <TextTitle bold sx={{ color: '$white' }}>
              {t(`global-bill-payment::authentication.title--google-authentication`)}
            </TextTitle>
          </View>
          <View sx={{ position: 'absolute', top: 0, right: 12, zIndex: 2 }}></View>
        </View>
      </PageHeaderModalNative>
      <ScrollView>
        <View sx={sxStyles.body}>
          <View sx={sxStyles.iconView}>
            <Icon name="google2FA" height={80} width={80} />
          </View>
          <TextBody sx={sxStyles.titleText}>{t('global-bill-payment::authentication.text--protect-your-accoount')}</TextBody>
          <View sx={sxStyles.otpView}>
            <TextCaption semiBold sx={sxStyles.otpText}>
              {t('global-bill-payment::authentication.text--enter-two-factor')}
            </TextCaption>
            <UniOtpInput
              errorText={pageState && !isEmpty(pageState.errorMessage) ? pageState.errorMessage : undefined}
              maxLength={OtpInput.MAX_LENGTH}
              onValueChange={(code) => {
                setPageState({ ...pageState, code, ready: code.length === OtpInput.MAX_LENGTH });
              }}
            />
          </View>
          {!isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={sxStyles.viewAlert} />}
        </View>
      </ScrollView>
      <View sx={sxStyles.viewRowButton} onLayout={onLayoutLocal}>
        <UniButton
          variant={ButtonVariant.outline1}
          labelSx={sxStyles.textReturn}
          sx={{ ...sxStyles.viewButton2, ...{ width: (sizeWidthButton - 16) / 2 } }}
          label={t('global-bill-payment::glossary.back')}
          onPress={onCancel}
        />
        <View sx={sxStyles.viewDividerHorizontal} />
        <UniButton
          sx={{ ...sxStyles.viewButton2, ...{ width: (sizeWidthButton - 16) / 2 } }}
          label={t('global-bill-payment::glossary.confirm')}
          onPress={() => onConfirm(pageState.code)}
          loading={isLoadingSubmit}
          disabled={isLoadingSubmit || !pageState.ready}
        />
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    height: '100%'
  },
  header: {
    flexDirection: 'column'
  },
  body: {
    pt: '$7',
    px: '$7',
    pb: '$10',
    zIndex: 2,
    alignItems: 'center'
  },
  iconView: {
    height: '$20',
    width: '$20',
    mb: '$2'
  },
  otpView: {
    mt: '$10'
  },
  titleText: {
    textAlign: 'center'
  },
  otpText: {
    mb: '$1'
  },
  viewAlert: {
    mt: '$3'
  },
  viewRowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: '$7',
    mx: '$7'
  },
  viewButton2: {
    width: '$60',
    height: '$11'
  },
  textReturn: {
    color: '$textPrimary'
  },
  viewDividerHorizontal: {
    width: '$4'
  }
};
